import type { InternalNote } from "@nlc/shared-types";

import { forwardRef, useEffect, useImperativeHandle } from "react";
import { Controller, useForm } from "react-hook-form";

import type { NoteFormValues } from "@/features/projects/project/internalNotes/types";

import { FieldError, Input, Label, TextField } from "@/components/field";
import { TiptapEditor } from "@/components/ui/TiptapEditor";

type UpdateNoteProps = {
  note: InternalNote | null;
  onSave: (noteId: number, values: NoteFormValues) => Promise<void>;
  onSuccess?: () => void;
};

export interface UpdateNoteHandle {
  submitForm: () => Promise<void>;
}

export const UpdateNote = forwardRef<UpdateNoteHandle, UpdateNoteProps>(
  ({ note, onSave, onSuccess }, ref) => {
    const { control, handleSubmit, reset } = useForm<NoteFormValues>({
      defaultValues: {
        description: note?.description ?? "",
        name: note?.name ?? "",
      },
    });

    useEffect(() => {
      reset({
        description: note?.description ?? "",
        name: note?.name ?? "",
      });
    }, [note, reset]);

    async function handleFormSubmit(values: NoteFormValues) {
      if (!note) return;

      await onSave(note.id, values);
      onSuccess?.();
    }

    useImperativeHandle(ref, () => ({
      submitForm: async () => {
        await handleSubmit(handleFormSubmit)();
      },
    }));

    return (
      <form className="flex flex-col gap-xl">
        <p className="font-bold text-xl">Edit note</p>

        <p className="text-text-tertiary">
          Add key information about the account or brand, such as subscription
          updates, requirements, or design guidelines.
        </p>

        <Controller
          control={control}
          name="name"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value}
            >
              <Label>Title</Label>
              <Input
                data-cy="internal-notes-name"
                placeholder="Add a short note name"
                ref={ref}
              />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
          rules={{ required: "Title is required" }}
        />

        <div className="flex flex-col gap-sm">
          <Label>Description</Label>

          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <TiptapEditor
                onChangeHTML={(html) => field.onChange(html)}
                placeholder="Enter your brand note to the design team"
                value={field.value}
              />
            )}
          />
        </div>
      </form>
    );
  },
);

UpdateNote.displayName = "UpdateNote";
